// components/AdminPortal/CoursesTable.jsx
import React, { useState } from "react";
import {
  Box,
  Table,
  TableHeader,
  TableRow,
  TableCell,
  TableBody,
  Text,
  Button,
  DropButton,
  Layer,
  Heading,
  Notification,
} from "grommet";
import {
  Archive,
  Document,
  View,
  More,
  Upload,
  Edit,
  Add,
} from "grommet-icons";
import StatusDot from "./StatusDot";
import adminAxios from "../../config/adminAxios";
import AddQuestionsModal from "./AddQuestionsModal";

const onViewDatasheet = (course) => {
  const courseId = course.course_id;
  if (courseId === "H45ZDS") {
    window.open(
      `https://www.hpe.com/us/en/collaterals/collateral.a50013061enw.html`,
      "_blank",
    );
  } else if (courseId === "H45ZCS") {
    window.open(
      `https://www.hpe.com/us/en/collaterals/collateral.a50013273enw.html`,
      "_blank",
    );
  } else if (course.OEM === "morpheus") {
    window.open(
      `https://education.hpe.com/ww/en/training/portfolio/virtualization.html`,
      "_blank",
    );
  } else {
    window.open(
      `https://education.hpe.com/ww/en/training/docs/cds/${courseId}.pdf`,
      "_blank",
    );

  }
};

const CoursesTable = ({
  courseData = [],
  onEdit,
  onViewQuestions,
  portfolio_id = portfolio_id,
}) => {
  // track which row’s menu is open by its index
  const [activeDropIndex, setActiveDropIndex] = useState(null);
  const [confirmUnpublishVisible, setConfirmUnpublishVisible] = useState(false);
  const [confirmPublishVisible, setConfirmPublishVisible] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");
  const [notificationStatus, setNotificationStatus] = useState("normal");
  const sortedCourseData = [...courseData].sort((a, b) => a.status - b.status);
  const [showAddQuestionsModal, setShowAddQuestionsModal] = useState(false);
  const [addModalCourse, setAddModalCourse] = useState(null);

  const openUnpublishModal = (course) => {
    setSelectedCourse(course);
    setConfirmUnpublishVisible(true);
  };

  const openPublishModal = (course) => {
    setSelectedCourse(course);
    setConfirmPublishVisible(true);
  };

  const handleConfirmUnpublish = async () => {
    try {
      const data = await adminAxios.patch(
        `/api/admin/unpublish-course/${selectedCourse.id}`,
      );
      setShowNotification(true);
      setNotificationMessage(
        data.data.message || "Course unpublished successfully",
      );
      setNotificationStatus("normal");
      setConfirmUnpublishVisible(false);
      setSelectedCourse(null);
      setTimeout(() => {
        setShowNotification(false);
        window.location.reload();
      }, 1500);
    } catch (err) {
      const errorMessage =
        err.response?.data?.error || "Unable to publish course";
      setShowNotification(true);
      setNotificationMessage(errorMessage);
      setNotificationStatus("critical");
      setTimeout(() => {
        setShowNotification(false);
      }, 3000);
      setConfirmPublishVisible(false);
    }
  };

  const handleConfirmPublish = async () => {
    try {
      const data = await adminAxios.patch(
        `/api/admin/publish-course/${selectedCourse.id}`,
      );
      setShowNotification(true);
      setNotificationMessage(
        data.data.message || "Course published successfully",
      );
      setNotificationStatus("normal");
      setConfirmPublishVisible(false);
      setSelectedCourse(null);
      setTimeout(() => {
        setShowNotification(false);
        window.location.reload();
      }, 1500);
    } catch (err) {
      const errorMessage =
        err.response?.data?.error || "Unable to publish course";
      setShowNotification(true);
      setNotificationMessage(errorMessage);
      setNotificationStatus("critical");
      setTimeout(() => {
        setShowNotification(false);
      }, 3000);
      setConfirmPublishVisible(false);
    }
  };

  return (
    <Box
      overflow="auto"
      background="light-1"
      round="small"
      pad="small"
      onScroll={() => {
        // close any open menu when scrolling
        setActiveDropIndex(null);
        setConfirmUnpublishVisible(false);
        setConfirmPublishVisible(false);
      }}
    >
      {showNotification && (
        <Notification
          toast
          status={notificationStatus}
          message={notificationMessage}
          onClose={() => setShowNotification(false)}
        />
      )}

      <Table style={{ tableLayout: "auto" }}>
        <TableHeader>
          <TableRow>
            <TableCell>
              <Text weight="bold" size="medium">
                Name
              </Text>
            </TableCell>
            <TableCell>
              <Text weight="bold" size="medium">
                Skill
              </Text>
            </TableCell>
            <TableCell>
              <Text weight="bold" size="medium">
                Total Questions
              </Text>
            </TableCell>
            <TableCell>
              <Text weight="bold" size="medium">
                Vendor
              </Text>
            </TableCell>
            <TableCell>
              <Text weight="bold" size="medium">
                Duration
              </Text>
            </TableCell>
            <TableCell>
              <Text weight="bold" size="medium">
                Tags
              </Text>
            </TableCell>
            <TableCell align="center">
              <Text weight="bold" size="medium">
                Actions
              </Text>
            </TableCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {courseData.length === 0 ? (
            <TableRow>
              <TableCell colSpan="7" align="center">
                No courses found.
              </TableCell>
            </TableRow>
          ) : (
            sortedCourseData.map((course, idx) => (
              <TableRow key={course.course_id}>
                <TableCell>
                  <Box direction="row" align="center" gap="small">
                    <StatusDot status={course.status} />
                    <Text>
                      {course.course_id} - {course.name}
                    </Text>
                  </Box>
                </TableCell>
                <TableCell>
                  <Text>{course.skill_names}</Text>
                </TableCell>
                <TableCell>
                  <Text>{course.question_count}</Text>
                </TableCell>
                <TableCell>
                  <Text>{course.OEM}</Text>
                </TableCell>
                <TableCell>
                  <Text>{course.duration}</Text>
                </TableCell>
                <TableCell>
                  <Text>
                    {course.isElearning === 0
                      ? "ILT/VILT"
                      : course.isElearning === 1
                        ? "eLearning"
                        : "eLearning/ILT/VILT"}{" "}
                    |{" "}
                    {course.level === 2
                      ? "Intermediate"
                      : course.level === 1
                        ? "Foundation"
                        : "Prerequisite"}
                  </Text>
                </TableCell>
                <TableCell align="center">
                  <DropButton
                    icon={<More color="brand" />}
                    dropAlign={{ top: "bottom", right: "right" }}
                    hoverIndicator
                    open={activeDropIndex === idx}
                    onOpen={() => setActiveDropIndex(idx)}
                    onClose={() => setActiveDropIndex(null)}
                    dropContent={
                      <Box pad="small" gap="small" background="light-2">
                        <Button
                          hoverIndicator
                          onClick={() => {
                            onViewQuestions(course);
                            setActiveDropIndex(null);
                          }}
                        >
                          <Box
                            direction="row"
                            gap="small"
                            align="center"
                            pad="xsmall"
                          >
                            <View color="brand" />
                            <Text>View Questions</Text>
                          </Box>
                        </Button>
                        <Button
                          hoverIndicator
                          onClick={() => {
                            onViewDatasheet(course);
                            setActiveDropIndex(null);
                          }}
                        >
                          <Box
                            direction="row"
                            gap="small"
                            align="center"
                            pad="xsmall"
                          >
                            <Document color="brand" />
                            <Text>View Datasheet</Text>
                          </Box>
                        </Button>
                        <Button
                          hoverIndicator
                          onClick={() => {
                            setAddModalCourse(course);
                            setShowAddQuestionsModal(true);
                            setActiveDropIndex(null);
                          }}
                        >
                          <Box
                            direction="row"
                            gap="small"
                            align="center"
                            pad="xsmall"
                          >
                            <Add color="brand" />
                            <Text>Add Question</Text>
                          </Box>
                        </Button>
                        {course.status === 1 ? (
                          <Button
                            hoverIndicator
                            onClick={() => {
                              openUnpublishModal(course);
                              setActiveDropIndex(null);
                            }}
                          >
                            <Box
                              direction="row"
                              gap="small"
                              align="center"
                              pad="xsmall"
                            >
                              <Archive color="status-critical" />
                              <Text>Unpublish</Text>
                            </Box>
                          </Button>
                        ) : (
                          <Button
                            hoverIndicator
                            onClick={() => {
                              openPublishModal(course);
                              setActiveDropIndex(null);
                            }}
                          >
                            <Box
                              direction="row"
                              gap="small"
                              align="center"
                              pad="xsmall"
                            >
                              <Upload color="brand" />
                              <Text>Publish</Text>
                            </Box>
                          </Button>
                        )}
                      </Box>
                    }
                  />
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {showAddQuestionsModal && addModalCourse && (
        <AddQuestionsModal
          open={showAddQuestionsModal}
          onClose={() => setShowAddQuestionsModal(false)}
          portfolioId={portfolio_id}
          courseId={addModalCourse.id}
          courseName={addModalCourse.name}
          existingQuestionIds={[]}
          onAdded={() => {
            setShowAddQuestionsModal(false);
            setShowNotification(true);
            setNotificationStatus("normal");
            setNotificationMessage("Question(s) added successfully.");
            setTimeout(() => {
              setShowNotification(false);
              window.location.reload();
            }, 2000);
          }}
        />
      )}

      {/* Unpublish Confirmation */}
      {confirmUnpublishVisible && selectedCourse && (
        <Layer
          onEsc={() => setConfirmUnpublishVisible(false)}
          onClickOutside={() => setConfirmUnpublishVisible(false)}
        >
          <Box pad="medium" gap="small" width="medium">
            <Heading level={3} margin="none">
              Unpublish Course
            </Heading>
            <Text>Are you sure you want to unpublish this course?</Text>
            <Box pad={{ vertical: "small" }}>
              <Text weight="bold">
                {selectedCourse.course_id} - {selectedCourse.name}
              </Text>
              <Text size="small" color="dark-5">
                Duration: {selectedCourse.duration} | Vendor:{" "}
                {selectedCourse.OEM}
              </Text>
            </Box>
            <Box direction="row" justify="end" gap="small">
              <Button
                label="No"
                onClick={() => setConfirmUnpublishVisible(false)}
              />
              <Button label="Yes" primary onClick={handleConfirmUnpublish} />
            </Box>
          </Box>
        </Layer>
      )}

      {/* Publish Confirmation */}
      {confirmPublishVisible && selectedCourse && (
        <Layer
          onEsc={() => setConfirmPublishVisible(false)}
          onClickOutside={() => setConfirmPublishVisible(false)}
        >
          <Box pad="medium" gap="small" width="medium">
            <Heading level={3} margin="none">
              Publish Course
            </Heading>
            <Text>Are you sure you want to publish this course?</Text>
            <Box pad={{ vertical: "small" }}>
              <Text weight="bold">
                {selectedCourse.course_id} - {selectedCourse.name}
              </Text>
              <Text size="small" color="dark-5">
                Duration: {selectedCourse.duration} | Vendor:{" "}
                {selectedCourse.OEM}
              </Text>
            </Box>
            <Box direction="row" justify="end" gap="small">
              <Button
                label="No"
                onClick={() => setConfirmPublishVisible(false)}
              />
              <Button label="Yes" primary onClick={handleConfirmPublish} />
            </Box>
          </Box>
        </Layer>
      )}
    </Box>
  );
};

export default CoursesTable;
